import { Link } from "@tanstack/solid-router";
import { For, Show, createEffect, createMemo, createSignal, on } from "solid-js";

import { apiRequest } from "~/lib/api";
import { useDeals, useMoveDealToStage, useStages } from "~/lib/hooks";
import type { DealListItem, DealRead, DealStatus, StageRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
  pipelineId: () => string | undefined;
  statusFilter: () => DealStatus | "";
};

function formatMoney(amount: number | null, currency: string | null): string {
  if (amount === null) return "";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? ""}`.trim();
  }
}

const COLUMN_HEAD = (stage: StageRead): string => {
  switch (stage.stage_type) {
    case "won":
      return "bg-success/10 border-success/40";
    case "lost":
      return "bg-error/10 border-error/40";
    default:
      return "bg-base-200 border-base-300";
  }
};

export function KanbanBoard(props: Props) {
  const stages = useStages(props.wsKey, props.pipelineId);
  const deals = useDeals(props.wsKey, () => ({
    pipeline_id: props.pipelineId(),
    status: props.statusFilter() || undefined,
    limit: 200,
  }));

  const moveMutation = useMoveDealToStage(props.wsKey);
  const [moveError, setMoveError] = createSignal<string | null>(null);
  const [dragOverStage, setDragOverStage] = createSignal<string | null>(null);
  const [announcement, setAnnouncement] = createSignal("");

  // Roving tabindex: the board is one Tab stop. `activeDealId` names the
  // currently-focusable card; every other card has tabindex=-1. The active
  // id updates on focus, on arrow ↑↓, and after a move.
  const [activeDealId, setActiveDealId] = createSignal<string | null>(null);
  const [refocusDealId, setRefocusDealId] = createSignal<string | null>(null);

  const dealsByStage = createMemo(() => {
    const grouped: Record<string, DealListItem[]> = {};
    for (const stage of stages.data ?? []) {
      grouped[stage.id] = [];
    }
    for (const deal of deals.data?.items ?? []) {
      (grouped[deal.stage_id] ??= []).push(deal);
    }
    return grouped;
  });

  const orderedStages = createMemo(() =>
    (stages.data ?? []).slice().sort((a, b) => a.order_index - b.order_index),
  );

  // Pick a default active card when deals first land or the active one
  // disappears (filter change, archived, etc.).
  createEffect(
    on(
      () => deals.data?.items,
      (items) => {
        if (!items) return;
        const current = activeDealId();
        if (current && items.some((d) => d.id === current)) return;
        // Walk columns in order and pick the first deal we find.
        for (const stage of orderedStages()) {
          const first = (dealsByStage()[stage.id] ?? [])[0];
          if (first) {
            setActiveDealId(first.id);
            return;
          }
        }
        setActiveDealId(null);
      },
    ),
  );

  // After a move, re-focus the moved card (now in its new column).
  createEffect(
    on(
      () => deals.data?.items,
      () => {
        const target = refocusDealId();
        if (!target) return;
        queueMicrotask(() => {
          const el = document.querySelector<HTMLElement>(`[data-deal-card="${target}"]`);
          if (el) el.focus();
          setRefocusDealId(null);
        });
      },
    ),
  );

  const moveDealToStage = async (deal: DealListItem, targetStage: StageRead): Promise<void> => {
    setMoveError(null);
    try {
      const dealRead = await apiRequest<DealRead>(
        `/v1/workspaces/${props.wsKey()}/deals/${deal.id}`,
      );
      await moveMutation.mutateAsync({
        dealId: deal.id,
        stage_id: targetStage.id,
        version: dealRead.version,
      });
      setAnnouncement(
        `Moved "${deal.name}" to ${targetStage.name}, probability ${targetStage.probability_pct}%.`,
      );
      setActiveDealId(deal.id);
      setRefocusDealId(deal.id);
    } catch (err) {
      setMoveError((err as Error).message);
    }
  };

  // ── HTML5 drag-and-drop ────────────────────────────────────────────────────
  const handleDragStart = (e: DragEvent, deal: DealListItem) => {
    if (!e.dataTransfer) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(
      "application/x-scaicrm-deal",
      JSON.stringify({ id: deal.id, stage_id: deal.stage_id }),
    );
  };

  const handleDragOver = (e: DragEvent, stageId: string) => {
    if (!e.dataTransfer) return;
    if (!e.dataTransfer.types.includes("application/x-scaicrm-deal")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stageId);
  };

  const handleDragLeave = (stageId: string) => {
    if (dragOverStage() === stageId) setDragOverStage(null);
  };

  const handleDrop = (e: DragEvent, targetStage: StageRead) => {
    e.preventDefault();
    setDragOverStage(null);
    const raw = e.dataTransfer?.getData("application/x-scaicrm-deal");
    if (!raw) return;
    let payload: { id: string; stage_id: string };
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    if (payload.stage_id === targetStage.id) return;
    const fullDeal = deals.data?.items.find((d) => d.id === payload.id);
    if (!fullDeal) return;
    void moveDealToStage(fullDeal, targetStage);
  };

  // ── Keyboard handlers per card ─────────────────────────────────────────────
  const handleCardKeyDown = (e: KeyboardEvent, deal: DealListItem): void => {
    // Space — link doesn't activate on Space by default, but our cards behave
    // button-like. Modifier-Space stays as a default scroll for parity with
    // the surrounding page.
    if (e.key === " " && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
      e.preventDefault();
      (e.currentTarget as HTMLElement).click();
      return;
    }

    // Enter is the link's native activation — let it through. The browser will
    // also honor Cmd/Ctrl+Enter and middle/right-click to open in a new tab.

    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      const ordered = orderedStages();
      const currentIdx = ordered.findIndex((s) => s.id === deal.stage_id);
      const delta = e.key === "ArrowLeft" ? -1 : 1;
      const targetIdx = currentIdx + delta;
      if (targetIdx < 0 || targetIdx >= ordered.length) return;
      void moveDealToStage(deal, ordered[targetIdx]);
      return;
    }

    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const stageDeals = dealsByStage()[deal.stage_id] ?? [];
      const idxInColumn = stageDeals.findIndex((d) => d.id === deal.id);
      const neighbour = stageDeals[idxInColumn + (e.key === "ArrowUp" ? -1 : 1)];
      if (!neighbour) return;
      setActiveDealId(neighbour.id);
      const el = document.querySelector<HTMLElement>(`[data-deal-card="${neighbour.id}"]`);
      el?.focus();
    }
  };

  return (
    <div class="flex flex-col gap-2">
      <div role="status" aria-live="polite" class="sr-only">
        {announcement()}
      </div>

      <Show when={moveError()}>
        <div role="alert" class="alert alert-error text-sm">
          {moveError()}
        </div>
      </Show>

      <Show
        when={!deals.isLoading && !stages.isLoading}
        fallback={
          <div role="status" aria-live="polite" class="text-center py-8 opacity-60">
            Loading board…
          </div>
        }
      >
        <div class="flex gap-3 overflow-x-auto pb-4">
          <For each={orderedStages()}>
            {(stage) => (
              <div
                class={`rounded-lg border ${COLUMN_HEAD(stage)} ${
                  dragOverStage() === stage.id ? "ring-2 ring-primary" : ""
                } w-72 shrink-0 flex flex-col`}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={() => handleDragLeave(stage.id)}
                onDrop={(e) => handleDrop(e, stage)}
                aria-label={`Stage ${stage.name}`}
              >
                <div class="p-3 border-b border-base-300 flex items-center justify-between">
                  <div class="font-medium">{stage.name}</div>
                  <div class="text-xs opacity-60">{dealsByStage()[stage.id]?.length ?? 0}</div>
                </div>
                <div class="flex flex-col gap-2 p-2 min-h-[12rem]">
                  <For each={dealsByStage()[stage.id] ?? []}>
                    {(deal) => (
                      <Link
                        to="/workspaces/$wsKey/deals/$dealId"
                        params={{
                          wsKey: props.wsKey(),
                          dealId: deal.id,
                        }}
                        data-deal-card={deal.id}
                        draggable
                        tabindex={activeDealId() === deal.id ? 0 : -1}
                        onFocus={() => setActiveDealId(deal.id)}
                        onDragStart={(e) => handleDragStart(e, deal)}
                        onKeyDown={(e) => handleCardKeyDown(e, deal)}
                        aria-label={`Deal ${deal.name}, in ${stage.name}. ${formatMoney(deal.amount, deal.currency)}.`}
                        class="card bg-base-100 shadow-sm border border-base-300 p-3 no-underline text-base-content cursor-grab active:cursor-grabbing hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 transition-shadow"
                      >
                        <div class="font-medium text-sm">{deal.name}</div>
                        <div class="flex items-center justify-between mt-1 text-xs opacity-70">
                          <span class="font-semibold">
                            {formatMoney(deal.amount, deal.currency)}
                          </span>
                          <Show when={deal.expected_close_date}>
                            <span>{new Date(deal.expected_close_date!).toLocaleDateString()}</span>
                          </Show>
                        </div>
                      </Link>
                    )}
                  </For>
                  <Show when={(dealsByStage()[stage.id]?.length ?? 0) === 0}>
                    <div class="text-center text-xs opacity-40 py-4">Drop deals here</div>
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>

      <div class="text-xs opacity-60 mt-1">
        <strong>Keyboard:</strong> <kbd class="kbd kbd-xs">Tab</kbd> into the board, then{" "}
        <kbd class="kbd kbd-xs">←</kbd> / <kbd class="kbd kbd-xs">→</kbd> to move between stages,{" "}
        <kbd class="kbd kbd-xs">↑</kbd> / <kbd class="kbd kbd-xs">↓</kbd> to focus between deals in
        a stage,
        <kbd class="kbd kbd-xs">Enter</kbd> to open. <strong>Mouse:</strong> drag a card, or
        right-click / Ctrl-click to open in a new tab.
      </div>
    </div>
  );
}
